import { ArrowRight, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/sonner'

export interface CreateCaseSchemaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: 'manual' | 'agent'
  /** Return false on failure to keep the name prompt open for retry. */
  onCreate: (name: string) => Promise<boolean>
}

/** Per-mode copy and submit icon: `agent` hands the new schema straight to the schema agent. */
const MODE = {
  manual: {
    description: 'schemaAgent.empty.manualDescription',
    submit: 'schemaAgent.empty.create',
    Icon: Plus,
  },
  agent: {
    description: 'schemaAgent.empty.nameDescription',
    submit: 'schemaAgent.empty.continueToAgent',
    Icon: ArrowRight,
  },
} as const

/** Name-prompt modal for creating a new case-owned schema (Person-seeded). */
export function CreateCaseSchemaDialog({
  open,
  onOpenChange,
  onCreate,
  mode = 'manual',
}: CreateCaseSchemaDialogProps) {
  const { t } = useTranslation()
  const copy = MODE[mode]
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const close = (next: boolean) => {
    if (busy) return
    if (!next) setName('')
    onOpenChange(next)
  }

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      if (await onCreate(trimmed)) {
        setName('')
        onOpenChange(false)
      }
    } catch {
      toast.error(t('schemaAgent.empty.createError'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {t('schemaAgent.empty.createTitle')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t(copy.description)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="case-schema-name" className="text-foreground">
            {t('schemaAgent.empty.nameLabel')}
          </Label>
          <Input
            id="case-schema-name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submit()
            }}
            placeholder={t('schemaAgent.empty.namePlaceholder')}
            className="bg-muted border-border"
            // biome-ignore lint/a11y/noAutofocus: name is the sole field of an intentional prompt
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => close(false)}
            disabled={busy}
            className="border-border"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <copy.Icon className="h-4 w-4 mr-1" />
            )}
            {t(copy.submit)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
